import { Fragment, type ReactNode } from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { HomeIcon } from '@/components/sidebar/icons/SidebarIcons';
import { findTrail, type NavSection } from '@strivelabs/ui-blocks/v2';
import { getBreadcrumbRoot, getBreadcrumbData } from '@/constants/navigation';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';
import {
  usePageChromeTitle,
  usePageChromeTitleIcon,
  usePageChromeParents,
  useHeaderActionsSlot,
} from '@/components/layout/PageChrome';

/**
 * A full-height, slightly-widened pointer target on an otherwise text-height
 * link, added through a pseudo-element so it doesn't disturb truncation or the
 * row's layout. `-inset-x-1` stays under the `gap-2` between crumbs, so adjacent
 * targets never overlap.
 */
const HIT_AREA = "relative before:absolute before:-inset-y-4 before:-inset-x-1 before:content-['']";
const LINK_BASE =
  'rounded-v2-sm text-v2-muted-foreground transition-colors hover:text-v2-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring';

/**
 * The persistent breadcrumb header. Derives its trail from the nav tree so the
 * breadcrumb and the sidebar can never disagree; a dynamic leaf title (a report
 * name, a skill) and page-level actions come from PageChrome. Rendered once by
 * {@link AppShell}, above the routed content.
 *
 * A dynamic title is a leaf *beyond* the matched nav node — the whole trail
 * becomes ancestors and the title is the terminal crumb (Home ▸ Reports ▸ Q3
 * report), rather than replacing the matched node's own label.
 */
export function PageHeader({ sections }: { sections: NavSection[] }) {
  const { pathname } = useLocation();
  const title = usePageChromeTitle();
  const parents = usePageChromeParents();
  const titleIcon = usePageChromeTitleIcon();
  const setActionsSlot = useHeaderActionsSlot();
  const { href: homeHref } = getBreadcrumbRoot();
  const trackEvent = useTrack();

  // `level` says which crumb was used: whether people climb one step up
  // (parent) or jump to the top (home). The origin page comes free from
  // `route_pattern`.
  const trackCrumb = (level: 'home' | 'ancestor' | 'parent') => {
    trackEvent(ANALYTICS_EVENTS.BREADCRUMB_CLICKED, { level });
  };

  const trail = findTrail(sections, pathname);
  const hasDynamicLeaf = Boolean(title);
  const ancestors = hasDynamicLeaf ? trail : trail.slice(0, -1);
  const leafLabel = title ?? trail.at(-1)?.label ?? getBreadcrumbData(pathname).currentPageLabel;
  /* A section's own page is its last nav node, so the glyph it wears in the
     rail is the one the crumb wants. Only a page with a title of its own — a
     file, a report — brings an icon with it. */
  const leafIcon = hasDynamicLeaf ? titleIcon : trail.at(-1)?.icon;
  const isHome = Boolean(useMatch(homeHref));

  // The home lobby has no breadcrumb (Home Shell design): the greeting is the
  // page's own heading and the wash runs edge to edge under it.
  if (isHome) return null;

  /**
   * A nav node's own glyph, normalised to the crumb size.
   *
   * The sidebar sizes these itself, so they arrive at whatever it uses; the
   * trail needs them at the size the folder and leaf crumbs beside them wear.
   */
  const CrumbIcon = ({ children }: { children?: ReactNode }) =>
    children ? (
      <span aria-hidden className="flex shrink-0 items-center [&_svg]:size-3.5">
        {children}
      </span>
    ) : null;

  // Transparent on the sheet it sits in, so the crumb bar reads as the top of
  // that surface rather than a band laid across it.
  return (
    <header className="flex h-10 shrink-0 items-center gap-2 border-b border-black/[0.045] pl-4 pr-5 dark:border-white/[0.06]">
      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
        <Link
          to={homeHref}
          onClick={() => trackCrumb('home')}
          aria-label="Home"
          className={`${HIT_AREA} ${LINK_BASE} shrink-0`}
        >
          {/* The rail's own house, not lucide's: the two sit a few pixels
              apart on the same screen, and one of them had a door. */}
          <HomeIcon className="size-4" />
        </Link>
        {ancestors.map((node) => (
          <Fragment key={node.id}>
            <ChevronRight aria-hidden className="size-3.5 shrink-0 text-v2-muted-foreground/60" />
            {node.href && node.href !== '#' ? (
              <Link
                to={node.href}
                onClick={() => trackCrumb('ancestor')}
                className={`${HIT_AREA} ${LINK_BASE} flex min-w-0 items-center gap-1.5`}
              >
                <CrumbIcon>{node.icon}</CrumbIcon>
                <span className="min-w-0 truncate">{node.label}</span>
              </Link>
            ) : (
              <span className="flex min-w-0 items-center gap-1.5 text-v2-muted-foreground">
                <CrumbIcon>{node.icon}</CrumbIcon>
                <span className="min-w-0 truncate">{node.label}</span>
              </span>
            )}
          </Fragment>
        ))}
        {parents.map((crumb) => (
          <Fragment key={`${crumb.label}-${crumb.href ?? ''}`}>
            <ChevronRight aria-hidden className="size-3.5 shrink-0 text-v2-muted-foreground/60" />
            {crumb.href ? (
              <Link
                to={crumb.href}
                onClick={() => trackCrumb('parent')}
                className={`${HIT_AREA} ${LINK_BASE} flex min-w-0 items-center gap-1.5`}
              >
                <CrumbIcon>{crumb.icon}</CrumbIcon>
                <span className="min-w-0 truncate">{crumb.label}</span>
              </Link>
            ) : (
              <span className="flex min-w-0 items-center gap-1.5 text-v2-muted-foreground">
                <CrumbIcon>{crumb.icon}</CrumbIcon>
                <span className="min-w-0 truncate">{crumb.label}</span>
              </span>
            )}
          </Fragment>
        ))}
        {leafLabel ? (
          <>
            <ChevronRight aria-hidden className="size-3.5 shrink-0 text-v2-muted-foreground/60" />
            <span
              aria-current="page"
              className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-v2-foreground"
              title={leafLabel}
            >
              <CrumbIcon>{leafIcon}</CrumbIcon>
              <span className="min-w-0 truncate">{leafLabel}</span>
            </span>
          </>
        ) : null}
      </nav>
      <div ref={setActionsSlot} className="flex shrink-0 items-center gap-2" />
    </header>
  );
}
